import { useId } from 'react';

/**
 * The appLockr brand loader.
 *
 * Ported from the platform's own `BrandLoader` (chimera-web
 * `components/Loader/BrandLoader.tsx`) so a report loading inside the platform
 * looks like the platform loading, rather than like a third-party embed with
 * its own spinner. The geometry and the 12s gradient sweep are the originals.
 *
 * Two things had to change for this context:
 *
 * The platform hard-codes the mark's body to `#221c35`, which is very close to
 * this app's dark surface (`#1a1529`) — the logo would all but disappear in dark
 * mode. The body is driven from `--ink` instead, so it stays `#221c35` in light
 * (identical to the platform) and inverts in dark. That means the pulse is a CSS
 * animation rather than the original SMIL, because SMIL `values` cannot
 * reference a custom property.
 *
 * The gradient sweeps on a short continuous cycle (2.4s, no off-screen holds),
 * and the inner blocks ALSO carry a CSS shimmer — so the mark is visibly
 * animating whether or not the browser runs SMIL. The original's 12s cycle with
 * long holds read as a static image on a quick load.
 *
 * The gradient's `id` is per-instance. The original is a fixed
 * `id="linear-gradient"`, which is fine when one loader is on screen and wrong
 * here: a dashboard shows a loader per tile, and duplicate SVG ids mean every
 * copy resolves its fill against the first one in the document.
 */

interface Props {
  /** Rendered size in px. Chart tiles want something far smaller than a page. */
  size?: number;
  label?: string;
}

export const BrandLoader = ({ size = 56, label = 'Loading' }: Props) => {
  const gradientId = useId();

  return (
    <span className="brand-loader" role="status" aria-label={label}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="37.11"
            y1="82.53"
            x2="93.96"
            y2="25.69"
            gradientUnits="userSpaceOnUse"
          >
            <animate
              attributeName="x1"
              values="120;-70;120"
              dur="2.4s"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
              repeatCount="indefinite"
            />
            <animate
              attributeName="x2"
              values="177;-13;177"
              dur="2.4s"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
              repeatCount="indefinite"
            />
            <stop offset="0" stopColor="#c8e2f5" />
            <stop offset=".18" stopColor="#a4d0ee" />
            <stop offset=".25" stopColor="#93c8ec" />
            <stop offset="1" stopColor="#673ea7" />
          </linearGradient>
        </defs>

        {/* The inner blocks, carrying the sweeping gradient. */}
        <path
          className="brand-loader__blocks"
          fill={`url(#${gradientId})`}
          d="M47,70.57h-21.13v-21.13h21.13v21.13ZM47,92.41v-19.41h-19.41l19.41,19.41ZM94.14,25.86h-21.13v21.13h21.13v-21.13ZM94.14,49.43h-21.13v21.13h21.13v-21.13ZM70.57,73h-21.13v21.13h21.13v-21.13ZM70.57,25.86h-21.13v21.13h21.13v-21.13ZM47,27.59l-19.41,19.41h19.41v-19.41Z"
        />
        {/* The disc and its cut-outs. Colour comes from CSS so it follows the theme. */}
        <path
          className="brand-loader__body"
          d="M60,0C26.86,0,0,26.86,0,60s26.86,60,60,60,60-26.86,60-60S93.14,0,60,0ZM47,49.43v21.13h-21.13v-21.13h21.13ZM27.59,47l19.41-19.41v19.41h-19.41ZM47,73v19.41l-19.41-19.41h19.41ZM70.57,73v21.13h-21.13v-21.13h21.13ZM49.43,47v-21.13h21.13v21.13h-21.13ZM73,70.57v-21.13h21.13v21.13h-21.13ZM94.14,47h-21.13v-21.13h21.13v21.13Z"
        />
      </svg>
    </span>
  );
};
